import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional ?next=/postcards — return the user where they came from after login.
  const rawNext = searchParams.get('next');
  const nextPath = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
  const { signInWithGoogle } = useAuth();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      console.log('User signed in successfully');
      navigate(nextPath);
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again. If the problem persists, try refreshing the page.');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(`Authentication failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      await signInWithGoogle();
      console.log('User signed in with Google successfully');
      navigate(nextPath);
    } catch (error) {
      console.error('Google sign in error:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(`Google sign-in failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const NAVY = '#112030', GREEN = '#4CAF50';
  const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 };
  const input = { width: '100%', padding: '12px 14px', border: '1px solid #dee2e6', borderRadius: 10, fontSize: 15, boxSizing: 'border-box' };

  return (
    <div className="content-section" style={{ background: '#eef2f6', boxShadow: 'none', padding: 0, borderRadius: 0 }}>
      <div style={{ minHeight: 'calc(100vh - 64px)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <img src="/mailvotes-logo.png" alt="MailVotes" style={{ height: 38, width: 'auto', marginBottom: 8 }} />
            <div style={{ fontSize: 12, color: '#94a3b8' }}>by Doornoc</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 16, padding: 32, boxShadow: '0 12px 32px rgba(16,42,67,.1)' }}>
            <h2 style={{ margin: '0 0 24px', textAlign: 'center', fontSize: 24, fontWeight: 900, color: NAVY }}>Sign in</h2>

            <form onSubmit={handleSignIn}>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="email" style={label}>Email</label>
                <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@campaign.com" style={input} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label htmlFor="password" style={label}>Password</label>
                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  placeholder="••••••••" style={input} />
              </div>

              {error && (
                <div style={{ color: '#991b1b', marginBottom: 16, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 14 }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading}
                style={{ width: '100%', border: 'none', background: loading ? '#94a3b8' : NAVY, color: '#fff', fontSize: 15, fontWeight: 700, borderRadius: 10, padding: '13px 0', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0', color: '#94a3b8' }}>
              <span style={{ flex: 1, height: 1, background: '#e5eaf0' }} /><span style={{ fontSize: 13 }}>or</span><span style={{ flex: 1, height: 1, background: '#e5eaf0' }} />
            </div>

            <button type="button" onClick={handleGoogleSignIn} disabled={loading}
              style={{ width: '100%', border: '1px solid #dee2e6', background: '#fff', color: NAVY, fontSize: 15, fontWeight: 600, borderRadius: 10, padding: '12px 0', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285f4" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z" />
                <path fill="#34a853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z" />
                <path fill="#fbbc05" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04z" />
                <path fill="#ea4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.92 2.26C4.6 5.05 6.62 3.48 9 3.48z" />
              </svg>
              {loading ? 'Signing in…' : 'Continue with Google'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <span style={{ fontSize: 14, color: '#475569' }}>Don't have an account? <Link to="/signup" style={{ color: GREEN, fontWeight: 700, textDecoration: 'none' }}>Sign Up</Link></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
